/**
 * ToolSurface — context wrapper that declares which viewport edge a region
 * of `Tool` triggers lives on. Tools read this to pick a flyout placement
 * that opens AWAY from the host edge, so a flyout never covers the strip
 * it came from.
 *
 * Place once around the content of each surface:
 *   <ToolSurface edge="left">  // ToolPalette, ViewportCameraControls
 *   <ToolSurface edge="bottom"> // ToolbarStrip, SidebarControls (expanded)
 *   <ToolSurface edge="right">  // SidebarControls (collapsed)
 *   <ToolSurface edge="top">    // HeaderBar
 *
 * Without a surrounding `ToolSurface`, flyouts fall back to `placement="below"`.
 */

import { createContext, type ReactNode, useContext } from "react";

export type ToolSurfaceEdge = "left" | "right" | "top" | "bottom";

type ToolSurfaceValue = { edge: ToolSurfaceEdge };

const ToolSurfaceContext = createContext<ToolSurfaceValue | null>(null);

export function ToolSurface({
  edge,
  children,
}: {
  edge: ToolSurfaceEdge;
  children: ReactNode;
}) {
  return (
    <ToolSurfaceContext.Provider value={{ edge }}>
      {children}
    </ToolSurfaceContext.Provider>
  );
}

export function useToolSurface(): ToolSurfaceValue | null {
  return useContext(ToolSurfaceContext);
}

/** Map the host surface edge to a flyout placement that opens away from it. */
export function flyoutPlacementForEdge(
  edge: ToolSurfaceEdge | null | undefined,
): "below" | "above" | "right" | "left" {
  switch (edge) {
    case "left":
      return "right";
    case "right":
      return "left";
    case "top":
      return "below";
    case "bottom":
      return "above";
    default:
      return "below";
  }
}
